import { apiSlice } from '@/redux/api.slice';
import type {
  PushDevice,
  PushPreferences,
  SubscribePushBody,
  UnsubscribePushBody,
  UpdatePushPreferencesBody
} from './push-subscription.types';

export const pushSubscriptionApi = apiSlice.injectEndpoints({
  // Dev-only: silences the "override without overrideExisting" warning that
  // Next dev's module re-evaluation triggers against the shared apiSlice
  // singleton — see the identical comment in redux/auth/auth.api.ts.
  overrideExisting: process.env.NODE_ENV !== 'production',
  endpoints: (builder) => ({
    getVapidPublicKey: builder.query<{ vapidPublicKey: string }, void>({
      query: () => 'api/push-subscriptions/vapid-public-key',
      transformResponse: (response: { data: { vapidPublicKey: string } }) =>
        response.data
    }),

    subscribePush: builder.mutation<{ id: string }, SubscribePushBody>({
      query: (body) => ({
        url: 'api/push-subscriptions',
        method: 'POST',
        body
      }),
      transformResponse: (response: { data: { id: string } }) => response.data,
      invalidatesTags: ['PushDevices', 'PushPreferences']
    }),

    unsubscribePush: builder.mutation<void, UnsubscribePushBody>({
      query: (body) => ({
        url: 'api/push-subscriptions',
        method: 'DELETE',
        body
      }),
      invalidatesTags: ['PushDevices', 'PushPreferences']
    }),

    listPushDevices: builder.query<PushDevice[], void>({
      query: () => 'api/push-subscriptions',
      transformResponse: (response: { data: PushDevice[] }) =>
        response.data ?? [],
      providesTags: ['PushDevices']
    }),

    getPushPreferences: builder.query<PushPreferences, void>({
      query: () => 'api/push-subscriptions/preferences',
      transformResponse: (response: { data: PushPreferences }) => response.data,
      providesTags: ['PushPreferences']
    }),

    updatePushPreferences: builder.mutation<
      PushPreferences,
      UpdatePushPreferencesBody
    >({
      query: (body) => ({
        url: 'api/push-subscriptions/preferences',
        method: 'PATCH',
        body
      }),
      transformResponse: (response: { data: PushPreferences }) => response.data,
      invalidatesTags: ['PushPreferences']
    })
  })
});

export const {
  useGetVapidPublicKeyQuery,
  useLazyGetVapidPublicKeyQuery,
  useSubscribePushMutation,
  useUnsubscribePushMutation,
  useListPushDevicesQuery,
  useGetPushPreferencesQuery,
  useUpdatePushPreferencesMutation
} = pushSubscriptionApi;
